import { useState, useEffect, useCallback } from "react";
import { Typography, Input, Button, Tag, Alert, Popconfirm, Tooltip, Select, message } from "antd";
import { UserAddOutlined, TeamOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import { useClient } from "../../context/ClientContext";

const { Text } = Typography;

// Qué hace cada rol, en palabras de la tienda.
const ROLES = [
  { value: 1, label: "Admin", color: "blue", ayuda: "Ve y maneja todo el panel." },
  { value: 3, label: "Vendedor", color: "green", ayuda: "Carga pedidos para clientes y ve los suyos." },
  { value: 4, label: "Tesorero", color: "gold", ayuda: "Maneja sus cuentas de transferencia y ve cuánto le entró." },
];
const rolDe = (id) => ROLES.find((r) => r.value === id) || ROLES[0];

const Section = ({ title, description, children }) => (
  <div style={{
    background: "#fff", border: "1px solid #e5e7eb",
    borderRadius: 8, padding: 24, marginBottom: 16,
  }}>
    {title && (
      <Text strong style={{ display: "block", fontSize: 16, marginBottom: description ? 4 : 20 }}>
        {title}
      </Text>
    )}
    {description && (
      <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 20 }}>
        {description}
      </Text>
    )}
    {children}
  </div>
);

const Field = ({ label, helper, children, style }) => (
  <div style={style}>
    {label && (
      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>
        {label}
      </label>
    )}
    {children}
    {helper && (
      <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
        {helper}
      </Text>
    )}
  </div>
);

export const Colaboradores = () => {
  const { theme } = useClient();
  const { user } = useAuth();
  const primary = theme?.primaryColor || "#0f172a";
  const primaryText = theme?.primaryTextColor || "#fff";

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ email: "", nombre: "", apellido: "", telefono: "", rol: 1 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const cargar = useCallback(async () => {
    setLoading(true);
    const res = await api.admin.listAdmins();
    if (res.status === "success") setAdmins(res.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    await api.admin.deleteAdmin(id);
    await cargar();
    setDeletingId(null);
  };

  const cambiarRol = async (a, rol) => {
    const res = await api.admin.cambiarRolColaborador(a.id, rol);
    if (res.status === "success") {
      message.success(res.message || "Rol actualizado");
      await cargar();
    } else {
      message.error(res.message || "No se pudo cambiar el rol");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const res = await api.admin.addAdmin(form);
    if (res.status === "success") {
      setMsg({ type: "success", text: res.message || "Colaborador agregado correctamente." });
      setForm({ email: "", nombre: "", apellido: "", telefono: "", rol: form.rol });
      await cargar();
    } else {
      setMsg({ type: "error", text: res.message || "No se pudo agregar el colaborador." });
    }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <TeamOutlined style={{ fontSize: 20, color: "#6b7280" }} />
        <Text strong style={{ fontSize: 22 }}>Colaboradores</Text>
      </div>

      {/* Lista */}
      <Section
        title="Colaboradores actuales"
        description="Quienes entran al panel de la tienda, cada uno con lo que le toca según su rol."
      >
        {loading ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Cargando...</Text>
        ) : admins.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Todavía no hay colaboradores cargados.</Text>
        ) : (
          <div>
            {admins.map((a, i) => (
              <div key={a.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0",
                borderBottom: i < admins.length - 1 ? "1px solid #f0f0f0" : "none",
              }}>
                <div>
                  <Text strong style={{ fontSize: 13 }}>{a.usuario}</Text>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                    {[a.nombre, a.apellido].filter(Boolean).join(" ") || "Sin nombre"}
                    {a.telefono && ` · ${a.telefono}`}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {a.usuario !== user?.usuario && (
                    <Popconfirm
                      title="¿Quitarle el rol a este colaborador?"
                      description={a.id_rol === 4
                        ? "Va a seguir teniendo cuenta, como cliente. Sus cuentas de transferencia pasan a ser de la tienda."
                        : "Va a seguir teniendo cuenta, como cliente."}
                      okText="Sí, quitar rol"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(a.id)}
                    >
                      <Tooltip title="Dar de baja">
                        <button type="button" disabled={deletingId === a.id} style={{
                          width: 28, height: 28, border: "1px solid #fca5a5",
                          borderRadius: "50%", background: "#fff",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          cursor: deletingId === a.id ? "not-allowed" : "pointer",
                          color: "#ef4444", fontSize: 13, opacity: deletingId === a.id ? 0.45 : 1,
                          transition: "border-color 0.15s",
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "#ef4444"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#fca5a5"; }}
                        >
                          <DeleteOutlined />
                        </button>
                      </Tooltip>
                    </Popconfirm>
                  )}
                  {a.usuario === user?.usuario ? (
                    <Tag color={rolDe(a.id_rol).color}>{rolDe(a.id_rol).label}</Tag>
                  ) : (
                    <Select
                      size="small"
                      value={a.id_rol}
                      onChange={(v) => cambiarRol(a, v)}
                      options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
                      style={{ width: 120 }}
                      aria-label="Rol del colaborador"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Formulario */}
      <Section title="Agregar colaborador" description="Ingresá el email y el rol. Si ya tiene cuenta en la tienda, se le asigna el rol; si no, entra con un código a ese mail.">
        <form onSubmit={handleSubmit}>
          <Field label="Rol" helper={rolDe(form.rol).ayuda} style={{ marginBottom: 16 }}>
            <Select
              value={form.rol}
              onChange={(v) => setForm((f) => ({ ...f, rol: v }))}
              options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
              style={{ maxWidth: 280, width: "100%" }}
            />
          </Field>
          <Field label="Email *" style={{ marginBottom: 16 }}>
            <Input
              type="email" required size="large"
              value={form.email} onChange={set("email")}
              placeholder="ejemplo@mail.com"
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Field label="Nombre">
              <Input value={form.nombre} onChange={set("nombre")} placeholder="Opcional" />
            </Field>
            <Field label="Apellido">
              <Input value={form.apellido} onChange={set("apellido")} placeholder="Opcional" />
            </Field>
          </div>
          <Field label="Teléfono" style={{ marginBottom: 20 }}>
            <Input value={form.telefono} onChange={set("telefono")} placeholder="Opcional" style={{ maxWidth: 280 }} />
          </Field>

          {msg && <Alert type={msg.type} message={msg.text} showIcon style={{ marginBottom: 16, fontSize: 13 }} />}

          <Button
            type="primary" htmlType="submit" loading={saving}
            icon={<UserAddOutlined />}
            style={{ background: primary, borderColor: primary, color: primaryText }}
          >
            {saving ? "Agregando..." : "Agregar colaborador"}
          </Button>
        </form>
      </Section>

    </div>
  );
};
